import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Menu, X, ChevronDown } from 'lucide-react';
import { siteInfo, navLinks } from '../data';
import './Header.css';

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      {/* Top Bar */}
      <div className="top-bar">
        <div className="container top-bar-inner">
          <span className="top-bar-label">24/7 Support Services</span>
          <div className="top-bar-contacts">
            <a href={`tel:${siteInfo.phone}`}>
              <Phone size={14} /> {siteInfo.phoneDisplay}
            </a>
            <a href={`mailto:${siteInfo.email}`}>
              <Mail size={14} /> {siteInfo.email}
            </a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={`header ${scrolled ? 'header--scrolled' : ''}`} ref={navRef}>
        <div className="container header-inner">
          <Link to="/" className="logo">
            <img src={siteInfo.logo} alt={siteInfo.name} />
          </Link>

          {/* Desktop Nav */}
          <nav className="nav-desktop">
            {navLinks.map((link) => (
              <div
                key={link.path}
                className="nav-item"
                onMouseEnter={() => link.children && setOpenDropdown(link.path)}
                onMouseLeave={() => link.children && setOpenDropdown(null)}
              >
                <Link
                  to={link.path}
                  className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                >
                  {link.label}
                  {link.children && <ChevronDown size={14} />}
                </Link>
                {link.children && openDropdown === link.path && (
                  <div className="dropdown">
                    {link.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className={`dropdown-link ${location.pathname === child.path ? 'active' : ''}`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Mobile Toggle */}
          <button
            className="mobile-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle Menu"
          >
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* Mobile Nav */}
        <div className={`nav-mobile ${mobileOpen ? 'open' : ''}`}>
          {navLinks.map((link) => (
            <div key={link.path} className="mobile-nav-item">
              <div className="mobile-nav-row">
                <Link to={link.path} className="mobile-nav-link">
                  {link.label}
                </Link>
                {link.children && (
                  <button
                    className="mobile-dropdown-toggle"
                    onClick={() =>
                      setOpenDropdown(openDropdown === link.path ? null : link.path)
                    }
                  >
                    <ChevronDown
                      size={18}
                      style={{
                        transform: openDropdown === link.path ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </button>
                )}
              </div>
              {link.children && openDropdown === link.path && (
                <div className="mobile-dropdown">
                  {link.children.map((child) => (
                    <Link key={child.path} to={child.path} className="mobile-dropdown-link">
                      {child.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </header>
    </>
  );
}
